import { useToast } from 'primevue/usetoast'
import type { ToastMessageOptions } from 'primevue/toast'

type PredefinedToast = keyof typeof predefined
type PredefinedExMap = typeof predefinedEx
type PredefinedToastEx = keyof PredefinedExMap
type ReplacementsFor<K extends PredefinedToastEx> = PredefinedExMap[K]['replacements']

const predefined = {
  saveConfirmation: {
    severity: 'success',
    life: 2500,
    summary: 'Gespeichert'
  },
  copySuccess: {
    severity: 'success',
    life: 2500,
    summary: 'Kopiert!'
  },
  addConfirmation: {
    severity: 'success',
    life: 2500,
    summary: 'Hinzugefügt'
  },
  deleteConfirmation: {
    severity: 'success',
    life: 2500,
    summary: 'Gelöscht'
  },
  notAllowed: {
    severity: 'warn',
    life: 2500,
    summary: 'Aktion ist nicht erlaubt'
  },
  restoreConfirmation: {
    severity: 'success',
    life: 2500,
    summary: 'Wiederhergestellt'
  },
} as const satisfies Record<string, ToastMessageOptions>
type Replacements = Record<string, string | number>
const predefinedEx = {
  effortCreatedConfirmation: {
    severity: 'success',
    life: 2500,
    summary: 'Objekt erzeugt - Titrop-Id %effortId%',
    replacements: {
      effortId: 0 as number
    }
  }
} as const satisfies Record<string, ToastMessageOptions & { replacements: Replacements }>

function applyReplacements(template: string, replacements: Record<string, string | number>): string {
  return Object.entries(replacements).reduce((acc, [key, value]) => {
    return acc.replaceAll(`%${key}%`, String(value))
  }, template)
}

export function useToastSimplified() {
  const toastService = useToast()

  function addPredefined(key: PredefinedToast, toastOverride?: ToastMessageOptions) {
    toastService.add(toastOverride ? { ...predefined[key], ...toastOverride } : predefined[key])
  }

  /**
   * Same as addPredefined - but support for replacements (NB: use %key% in summary templates)
   * @param key
   * @param replacements
   * @param toastOverride
   */
  function addPredefinedEx<K extends PredefinedToastEx>(key: K, replacements: ReplacementsFor<K>, toastOverride?: ToastMessageOptions) {
    const template = predefinedEx[key]
    const summary = applyReplacements(template.summary, replacements)
    toastService.add({
      ...template,
      summary,
      ...toastOverride
    })
  }

  function add(message: ToastMessageOptions): void {
    toastService.add(message)
  }

  function remove(message: ToastMessageOptions): void {
    toastService.remove(message)
  }

  function removeAllGroups(): void {
    toastService.removeAllGroups()
  }

  function removeGroup(group: string): void {
    toastService.removeGroup(group)
  }

  return {
    addPredefined,
    addPredefinedEx,
    add,
    remove,
    removeAllGroups,
    removeGroup
  }
}
